import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLanguage, useAuth, api, COUNTRIES } from '@/lib/context';
import { useTranslatedContent } from '@/hooks/useContentTranslation';
import { TranslatableText } from '@/components/TranslatableText';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Car, MapPin, Loader2, Plus, Euro, Search, Star, Phone, Mail, 
  CheckCircle2, Building2, Calendar, ArrowLeft, Send, Languages
} from 'lucide-react';
import { toast } from 'sonner';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { LockedContactCard } from '@/components/LockedContactCard';
import { ShareButton, ShareButtonsInline } from '@/components/ShareButton';
import { SEOHead } from '@/components/SEOHead';

// Auto service categories
const AUTO_SERVICE_CATEGORIES = [
  'tires',
  'diagnostics',
  'engine',
  'bodywork',
  'brakes',
  'oil_change',
  'transmission',
  'electrical',
  'ac_heating',
  'exhaust',
  'suspension',
  'general_repair'
];

// Shared Nav for auto services
const AutoServicesNav = () => {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold text-brand">KEIKKA</Link>
          
          <div className="hidden md:flex items-center gap-6">
            <Link to="/jobs" className="text-muted-foreground hover:text-foreground transition">
              {t('nav.jobs')}
            </Link>
            <Link to="/subcontracting" className="text-muted-foreground hover:text-foreground transition">
              {t('nav.subcontracting')}
            </Link>
            <Link to="/auto" className="text-foreground font-medium">
              {t('nav.autoServices')}
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            {user ? (
              <>
                <Link to={user.role === 'company' ? '/company/dashboard' : user.role === 'admin' ? '/admin' : '/worker/dashboard'}>
                  <Button variant="outline" size="sm" data-testid="nav-dashboard">{t('nav.dashboard')}</Button>
                </Link>
                <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }}>
                  {t('auth.logout')}
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm">{t('auth.login')}</Button>
                </Link>
                <Link to="/register">
                  <Button size="sm">{t('auth.register')}</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

// Service Card Component
const AutoServiceCard = ({ service, t }) => {
  const { text: translatedTitle, isTranslated: titleTranslated } = useTranslatedContent(service.title);
  const { text: translatedDesc, isTranslated: descTranslated } = useTranslatedContent(service.description);
  const isContentTranslated = titleTranslated || descTranslated;
  
  return (
    <Card className="card-hover h-full flex flex-col" data-testid={`service-card-${service.id}`}>
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start gap-2">
          <div className="flex-1">
            <CardTitle className="text-lg line-clamp-1">
              {translatedTitle}
              {isContentTranslated && (
                <Languages className="inline-block ml-2 h-3.5 w-3.5 text-blue-500" title={t('common.translated')} />
              )}
            </CardTitle>
            <CardDescription className="flex items-center gap-1 mt-1">
              <Building2 className="h-3 w-3" />
              {service.company_name || t('auto.workshop')}
            </CardDescription>
          </div>
          <ShareButton 
            entityType="auto_service" 
            entityId={service.id} 
            title={service.title}
            iconOnly
          />
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        <p className="text-sm text-muted-foreground line-clamp-3 mb-4">{translatedDesc}</p>
        <div className="flex flex-wrap gap-2">
          <Badge variant="secondary" className="flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            <TranslatableText text={service.location_city} />, {t(`location.countries.${service.location_country}`)}
          </Badge>
          {service.price_from && (
            <Badge className="bg-brand/10 text-brand border-brand/20">
              <Euro className="h-3 w-3 mr-1" />
              {t('auto.from')} €{service.price_from}
            </Badge>
          )}
          {service.rating > 0 && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
              {service.rating.toFixed(1)}
            </Badge>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex justify-between items-center">
        <ShareButtonsInline 
          entityType="auto_service" 
          entityId={service.id} 
          title={service.title}
        />
        <Link to={`/auto/${service.id}`}>
          <Button size="sm" data-testid={`view-service-${service.id}`}>
            {t('auto.viewDetails')}
          </Button>
        </Link>
      </CardFooter>
    </Card>
  );
};

// Browse Auto Services Page
export const AutoServicesPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [filters, setFilters] = useState({ category: 'all', country: 'all', city: '', q: '' });
  const [newService, setNewService] = useState({
    title: '',
    description: '',
    category: '',
    price_from: '',
    location_country: '',
    location_city: ''
  });

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') params[key] = value;
      });
      const res = await api.get('/auto/services', { params });
      setServices(res.data);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleCreateService = async () => {
    if (!user || user.role !== 'company') {
      toast.error(t('auto.companyOnly'));
      return;
    }

    if (!newService.title || !newService.description || !newService.category || 
        !newService.location_country || !newService.location_city) {
      toast.error(t('common.fillRequired'));
      return;
    }

    setCreating(true);
    try {
      const data = {
        ...newService,
        price_from: newService.price_from ? parseFloat(newService.price_from) : null
      };
      await api.post('/auto/services', data);
      toast.success(t('auto.serviceCreated'));
      setCreateOpen(false);
      setNewService({ title: '', description: '', category: '', price_from: '', location_country: '', location_city: '' });
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AutoServicesNav />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="auto-services-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Car className="h-8 w-8 text-brand" />
              {t('auto.title')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t('auto.subtitle')}
            </p>
          </div>
          {user?.role === 'company' && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-service-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  {t('auto.createService')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>{t('auto.createServiceTitle')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('auto.serviceTitle')} *</Label>
                    <Input
                      placeholder={t('auto.serviceTitlePlaceholder')}
                      value={newService.title}
                      onChange={(e) => setNewService({...newService, title: e.target.value})}
                      data-testid="service-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('auto.category')} *</Label>
                    <Select value={newService.category} onValueChange={(v) => setNewService({...newService, category: v})}>
                      <SelectTrigger data-testid="service-category-select">
                        <SelectValue placeholder={t('auto.selectCategory')} />
                      </SelectTrigger>
                      <SelectContent>
                        {AUTO_SERVICE_CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {t(`autoCategories.${cat}`) || cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('auto.description')} *</Label>
                    <Textarea
                      placeholder={t('auto.descriptionPlaceholder')}
                      value={newService.description}
                      onChange={(e) => setNewService({...newService, description: e.target.value})}
                      rows={3}
                      data-testid="service-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('jobs.country')} *</Label>
                      <Select value={newService.location_country} onValueChange={(v) => setNewService({...newService, location_country: v})}>
                        <SelectTrigger data-testid="service-country-select">
                          <SelectValue placeholder={t('auth.selectCountry')} />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map((c) => (
                            <SelectItem key={c.code} value={c.code}>{t(c.nameKey)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('auto.city')} *</Label>
                      <Input
                        placeholder={t('auto.cityPlaceholder')}
                        value={newService.location_city}
                        onChange={(e) => setNewService({...newService, location_city: e.target.value})}
                        data-testid="service-city-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('auto.priceFrom')}</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={newService.price_from}
                      onChange={(e) => setNewService({...newService, price_from: e.target.value})}
                      data-testid="service-price-input"
                    />
                    <p className="text-xs text-muted-foreground">{t('auto.priceHint')}</p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateOpen(false)}>{t('common.cancel')}</Button>
                  <Button onClick={handleCreateService} disabled={creating} data-testid="submit-service-btn">
                    {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('auto.createButton')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Filters */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="col-span-2 md:col-span-1">
                <Select value={filters.category} onValueChange={(v) => setFilters({...filters, category: v})}>
                  <SelectTrigger data-testid="filter-category">
                    <SelectValue placeholder={t('auto.category')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('marketplace.filters.allCategories')}</SelectItem>
                    {AUTO_SERVICE_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {t(`autoCategories.${cat}`) || cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Select value={filters.country} onValueChange={(v) => setFilters({...filters, country: v})}>
                <SelectTrigger data-testid="filter-country">
                  <SelectValue placeholder={t('jobs.country')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('marketplace.filters.allCountries')}</SelectItem>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{t(c.nameKey)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder={t('auto.city')}
                value={filters.city}
                onChange={(e) => setFilters({...filters, city: e.target.value})}
                data-testid="filter-city"
              />
              <Input
                placeholder={t('common.search')}
                value={filters.q}
                onChange={(e) => setFilters({...filters, q: e.target.value})}
                className="md:col-span-1"
                data-testid="filter-search"
              />
              <Button 
                variant="outline" 
                onClick={() => setFilters({ category: 'all', country: 'all', city: '', q: '' })}
              >
                {t('marketplace.filters.clear')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Services Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3,4,5,6].map((i) => (
              <Card key={i}><CardContent className="pt-6"><Skeleton className="h-48" /></CardContent></Card>
            ))}
          </div>
        ) : services.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Car className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-xl font-semibold mb-2">{t('auto.noServices')}</p>
              <p className="text-muted-foreground mb-6">{t('auto.noServicesDesc')}</p>
              {user?.role === 'company' && (
                <Button onClick={() => setCreateOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('auto.beFirst')}
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <AutoServiceCard key={service.id} service={service} t={t} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Service Detail Page
export const AutoServiceDetailPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [service, setService] = useState(null);
  const [seoData, setSeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestOpen, setRequestOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requestForm, setRequestForm] = useState({
    message: '',
    requester_name: '',
    requester_email: '',
    requester_phone: '',
    preferred_date: '',
    vehicle_info: ''
  });

  // Check if user just unlocked
  const justUnlocked = searchParams.get('unlocked') === 'true';

  const fetchService = useCallback(async () => {
    try {
      const res = await api.get(`/auto/services/${id}`);
      setService(res.data);
    } catch (error) {
      toast.error(t('common.error'));
      navigate('/auto');
    } finally {
      setLoading(false);
    }
  }, [id, t, navigate]);

  const fetchSeoData = useCallback(async () => {
    try {
      const res = await api.get(`/public/auto/${id}`);
      setSeoData(res.data);
    } catch (error) {
      console.log('SEO data not available');
    }
  }, [id]);

  useEffect(() => {
    fetchService();
    fetchSeoData();
  }, [fetchService, fetchSeoData]);

  useEffect(() => {
    if (user) {
      setRequestForm(prev => ({
        ...prev,
        requester_name: user.nickname,
        requester_email: user.email
      }));
    }
  }, [user]);

  useEffect(() => {
    if (justUnlocked) {
      toast.success(t('auto.contactUnlocked'));
    }
  }, [justUnlocked, t]);

  const handleSubmitRequest = async () => {
    if (!requestForm.message || !requestForm.requester_name || !requestForm.requester_email) {
      toast.error(t('common.fillRequired'));
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/auto/services/${id}/request`, requestForm);
      toast.success(t('auto.requestSent'));
      setRequestOpen(false);
      setRequestForm(prev => ({ ...prev, message: '', preferred_date: '', vehicle_info: '' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AutoServicesNav />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!service) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* SEO Head */}
      {seoData && (
        <SEOHead
          title={seoData.meta?.title || service?.title}
          description={seoData.meta?.description || service?.description?.substring(0, 160)}
          url={seoData.share_url || window.location.href}
          schema={seoData.schema}
          type="service"
        />
      )}
      
      <AutoServicesNav />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="auto-service-detail">
        {/* Back button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/auto')} 
          className="mb-6"
          data-testid="back-to-auto"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('auto.backToServices')}
        </Button>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-2xl">{service.title}</CardTitle>
                    <CardDescription className="flex items-center gap-2 mt-2">
                      <Building2 className="h-4 w-4" />
                      {service.company_name || t('auto.workshop')}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-sm">
                      {t(`autoCategories.${service.category}`) || service.category}
                    </Badge>
                    <ShareButton 
                      entityType="auto_service" 
                      entityId={service.id} 
                      title={service.title}
                      iconOnly
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground whitespace-pre-wrap">{service.description}</p>
                
                <div className="flex flex-wrap gap-3 pt-4 border-t">
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {service.location_city}, {service.location_country}
                  </Badge>
                  {service.price_from && (
                    <Badge className="bg-brand/10 text-brand border-brand/20">
                      <Euro className="h-4 w-4 mr-1" />
                      {t('auto.from')} €{service.price_from}
                    </Badge>
                  )}
                  {service.rating > 0 && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                      {service.rating.toFixed(1)} ({service.rating_count} {t('auto.reviews')})
                    </Badge>
                  )}
                </div>
                
                {/* Share Section */}
                <div className="pt-4 border-t flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('share.shareService')}</span>
                  <ShareButton entityType="auto_service" entityId={service.id} title={service.title} />
                </div>
              </CardContent>
            </Card>

            {/* Request Service Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5 text-brand" />
                  {t('auto.requestService')}
                </CardTitle>
                <CardDescription>
                  {t('auto.requestServiceDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full" data-testid="open-request-dialog">
                      <Calendar className="mr-2 h-4 w-4" />
                      {t('auto.sendRequest')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                      <DialogTitle>{t('auto.requestFormTitle')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('auto.yourName')} *</Label>
                          <Input
                            value={requestForm.requester_name}
                            onChange={(e) => setRequestForm({...requestForm, requester_name: e.target.value})}
                            placeholder={t('serviceRequest.yourNamePlaceholder')}
                            data-testid="request-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('auto.yourEmail')} *</Label>
                          <Input
                            type="email"
                            value={requestForm.requester_email}
                            onChange={(e) => setRequestForm({...requestForm, requester_email: e.target.value})}
                            placeholder="you@example.com"
                            data-testid="request-email-input"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('auto.phone')}</Label>
                          <Input
                            value={requestForm.requester_phone}
                            onChange={(e) => setRequestForm({...requestForm, requester_phone: e.target.value})}
                            placeholder="+358..."
                            data-testid="request-phone-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('auto.preferredDate')}</Label>
                          <Input
                            type="date"
                            value={requestForm.preferred_date}
                            onChange={(e) => setRequestForm({...requestForm, preferred_date: e.target.value})}
                            data-testid="request-date-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('auto.vehicleInfo')}</Label>
                        <Input
                          value={requestForm.vehicle_info}
                          onChange={(e) => setRequestForm({...requestForm, vehicle_info: e.target.value})}
                          placeholder={t('auto.vehiclePlaceholder')}
                          data-testid="request-vehicle-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('auto.message')} *</Label>
                        <Textarea
                          value={requestForm.message}
                          onChange={(e) => setRequestForm({...requestForm, message: e.target.value})}
                          placeholder={t('auto.messagePlaceholder')}
                          rows={4}
                          data-testid="request-message-input"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setRequestOpen(false)}>
                        {t('common.cancel')}
                      </Button>
                      <Button onClick={handleSubmitRequest} disabled={submitting} data-testid="submit-request-btn">
                        {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {t('auto.submitRequest')}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Contact */}
          <div className="space-y-6">
            <LockedContactCard
              targetUserId={service.company_id}
              targetUserName={service.company_name}
              targetUserRole="company"
              jobId={null}
              companyName={service.company_name}
              onUnlocked={() => {
                toast.success(t('auto.contactUnlocked'));
              }}
            />

            {/* Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {t('auto.serviceStats')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('auto.requests')}</span>
                  <span className="font-medium">{service.requests_count || 0}</span>
                </div>
                {service.rating > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('auto.rating')}</span>
                    <span className="font-medium flex items-center gap-1">
                      <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                      {service.rating.toFixed(1)}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoServicesPage;
